import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { api } from "../../lib/api";
import { ChangePassword } from "./ChangePassword";
import { MfaEnrol } from "./Mfa";

const AuthCtx = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);

  const check = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data ?? false);
    } catch {
      try {
        await api.post("/auth/refresh");
        const { data } = await api.get("/auth/me");
        setUser(data ?? false);
      } catch {
        setUser(false);
      }
    }
  }, []);

  useEffect(() => {
    check();
  }, [check]);

  const logout = async () => {
    try { await api.post("/auth/logout"); } finally { setUser(false); }
  };

  const can = useCallback((perm) => Boolean(user && Array.isArray(user.permissions) && user.permissions.includes(perm)), [user]);

  return <AuthCtx.Provider value={{ user, setUser, logout, can }}>{children}</AuthCtx.Provider>;
};

export const useAuth = () => useContext(AuthCtx);

export const RequireAdmin = ({ children }) => {
  const { user } = useAuth();
  if (user === null)
    return <div className="grid min-h-screen place-items-center bg-[#0C0A09]"><Loader2 className="h-6 w-6 animate-spin text-orange-400" /></div>;
  if (!user) return <Navigate to="/admin/login" replace />;
  if (user.must_change_password) return <ForcePasswordChange />;
  if (user.mfa_setup_required) return <MfaEnrol />;
  return children;
};

const ForcePasswordChange = () => (
  <div className="grid min-h-screen place-items-center bg-[#0C0A09] px-5" data-testid="force-password-change">
    <div className="w-full max-w-md">
      <p className="font-display text-2xl text-stone-100">Set your own password</p>
      <p className="mt-2 text-sm text-stone-400">You signed in with a temporary password. Choose a new one to continue.</p>
      <div className="mt-6"><ChangePassword /></div>
    </div>
  </div>
);
